import { useState, useEffect } from 'react';
import { getCustomFields, createCustomField, updateCustomField, deleteCustomField, getActivityTypes, createActivityType, updateActivityType, deleteActivityType, setupActivityTypes, getSystemSettings, updateSystemSettings, getEmailStatus, sendTestEmail, getBusinessUnits, createBusinessUnit, updateBusinessUnit, deleteBusinessUnit, getProducts, createProduct, updateProduct, deleteProduct, getCompensationTypes, createCompensationType, updateCompensationType, deleteCompensationType, getProductCompensations, createProductCompensation, updateProductCompensation, deleteProductCompensation, getProjectFunctions, PROJECT_FUNCTIONS, getStorageStats } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Trash2, Settings, Database, Tag, Loader2, X, Briefcase, Lock, FolderKanban, FileText, Building2, Calendar, Package, Pencil, Coins, DollarSign, Percent, Bell, HardDrive } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import StorageStatsPanel from '../components/StorageStatsPanel';

const fieldTypes = [
  { value: 'text', label: 'Testo', icon: 'Aa' },
  { value: 'number', label: 'Numero', icon: '#' },
  { value: 'date', label: 'Data', icon: '📅' },
  { value: 'select', label: 'Selezione', icon: '▼' },
  { value: 'multiselect', label: 'Selezione Multipla', icon: '☑' },
  { value: 'boolean', label: 'Sì/No', icon: '✓' },
];

const entityTypes = [
  { value: 'project', label: 'Progetto', icon: FolderKanban, color: 'bg-purple-100 text-purple-700' },
  { value: 'contract', label: 'Contratto', icon: FileText, color: 'bg-orange-100 text-orange-700' },
  { value: 'client', label: 'Cliente', icon: Building2, color: 'bg-orange-100 text-orange-700' },
  { value: 'success_fee', label: 'Success Fee & Vendite', icon: Coins, color: 'bg-emerald-100 text-emerald-700' },
];

export default function SettingsPage() {
  const [customFields, setCustomFields] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [products, setProducts] = useState([]);
  const [systemSettings, setSystemSettings] = useState({
    pm_cutoff_day: 6,
    venditore_cutoff_day: 3
  });
  const [emailStatus, setEmailStatus] = useState({ configured: false });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [buDialogOpen, setBuDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingBu, setEditingBu] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const { toast } = useToast();

  const [fieldFormData, setFieldFormData] = useState({
    name: '',
    field_type: 'text',
    entity_type: 'project',
    options: [],
    required: false,
  });
  const [newOption, setNewOption] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeProductIds, setNewTypeProductIds] = useState([]);
  const [newTypeFuncFees, setNewTypeFuncFees] = useState({});
  const [editingType, setEditingType] = useState(null);
  const [projectFunctions, setProjectFunctions] = useState([]);
  
  // Compensation types
  const [compensationTypes, setCompensationTypes] = useState([]);
  const [compTypeDialogOpen, setCompTypeDialogOpen] = useState(false);
  const [editingCompType, setEditingCompType] = useState(null);
  const [compTypeForm, setCompTypeForm] = useState({ name: '', bucket: 'fisso' });

  // Notification settings
  const [notifSettings, setNotifSettings] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [newCustomEmail, setNewCustomEmail] = useState('');

  
  // Product compensation config
  const [productCompDialogOpen, setProductCompDialogOpen] = useState(false);
  const [selectedProductForComp, setSelectedProductForComp] = useState(null);
  const [productComps, setProductComps] = useState([]);
  const [compForm, setCompForm] = useState({ project_function: '', compensation_type_id: '', amount: '', recurrence_type: 'una_tantum', recurrence_months: '', auto_freeze: true });
  const [editingComp, setEditingComp] = useState(null);
  
  // Business Unit form
  const [buFormData, setBuFormData] = useState({
    name: '',
    description: '',
  });
  
  // Product form
  const [productFormData, setProductFormData] = useState({
    name: '',
    business_unit_id: '',
    has_expiration: true,
    description: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Setup default activity types if needed
      await setupActivityTypes();
      
      const [fieldsRes, typesRes, settingsRes, emailRes, buRes, productsRes, compTypesRes, funcRes] = await Promise.all([
        getCustomFields(),
        getActivityTypes(),
        getSystemSettings(),
        getEmailStatus().catch(() => ({ data: { configured: false } })),
        getBusinessUnits(true),
        getProducts(true),
        getCompensationTypes(),
        getProjectFunctions().catch(() => ({ data: [] })),
      ]);
      setCustomFields(fieldsRes.data);
      setActivityTypes(typesRes.data);
      setSystemSettings(settingsRes.data);
      setEmailStatus(emailRes.data);
      setBusinessUnits(buRes.data);
      setProducts(productsRes.data);
      setCompensationTypes(compTypesRes.data);
      // Merge API result with hardcoded PROJECT_FUNCTIONS fallback so the user always sees the
      // complete set, even if the DB lost some entries (defensive fix for production data drift).
      const apiFuncs = funcRes.data || [];
      const seen = new Set(apiFuncs.map(f => f.value));
      const merged = [...apiFuncs];
      for (const f of PROJECT_FUNCTIONS) {
        if (!seen.has(f.value)) merged.push({ value: f.value, label: f.label });
      }
      setProjectFunctions(merged);
      
      // Load notification settings
      try {
        const notifRes = await api.get('/notification-settings');
        setNotifSettings(notifRes.data || []);
      } catch { /* ignore if not admin */ }
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare le impostazioni', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Custom Fields handlers
  const handleOpenFieldDialog = (fieldOrEvent = null) => {
    // Ignora se è un evento di click (quando si clicca sul pulsante "Nuovo Campo")
    const field = (fieldOrEvent && fieldOrEvent.target) ? null : fieldOrEvent;
    
    if (field && field.id) {
      // Editing existing field
      setEditingField(field);
      setFieldFormData({
        name: field.name,
        field_type: field.field_type,
        entity_type: field.entity_type,
        options: field.options || [],
        required: field.is_required || false,
      });
    } else {
      // Creating new field
      setEditingField(null);
      setFieldFormData({
        name: '',
        field_type: 'text',
        entity_type: 'project',
        options: [],
        required: false,
      });
    }
    setNewOption('');
    setFieldDialogOpen(true);
  };

  const handleAddOption = () => {
    if (newOption.trim() && !fieldFormData.options.includes(newOption.trim())) {
      setFieldFormData({ ...fieldFormData, options: [...fieldFormData.options, newOption.trim()] });
      setNewOption('');
    }
  };

  const handleRemoveOption = (opt) => {
    setFieldFormData({ ...fieldFormData, options: fieldFormData.options.filter((o) => o !== opt) });
  };

  const handleSubmitField = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingField) {
        // Update existing field
        await updateCustomField(editingField.id, {
          name: fieldFormData.name,
          options: fieldFormData.options,
          is_required: fieldFormData.required,
        });
        toast({ title: 'Campo personalizzato aggiornato' });
      } else {
        // Create new field
        await createCustomField({
          ...fieldFormData,
          is_required: fieldFormData.required,
        });
        toast({ title: 'Campo personalizzato creato' });
      }
      setFieldDialogOpen(false);
      setEditingField(null);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteField = async (field) => {
    if (!window.confirm(`Eliminare il campo "${field.name}"?`)) return;
    try {
      await deleteCustomField(field.id);
      toast({ title: 'Campo eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare il campo', variant: 'destructive' });
    }
  };

  // Activity Types handlers
  const handleSubmitType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) return;
    const totalPct = Object.values(newTypeFuncFees).reduce((s, v) => s + (v || 0), 0);
    if (totalPct > 100) {
      toast({ title: 'Errore', description: 'La somma delle % non può superare il 100%', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = { 
        name: newTypeName.trim(),
        product_ids: newTypeProductIds,
        function_fee_percentages: newTypeFuncFees
      };
      if (editingType) {
        await updateActivityType(editingType.id, payload);
        toast({ title: 'Tipologia aggiornata' });
      } else {
        await createActivityType(payload);
        toast({ title: 'Tipologia creata' });
      }
      setNewTypeName('');
      setNewTypeProductIds([]);
      setNewTypeFuncFees({});
      setEditingType(null);
      setTypeDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditType = (type) => {
    setEditingType(type);
    setNewTypeName(type.name);
    setNewTypeProductIds(type.product_ids || []);
    setNewTypeFuncFees(type.function_fee_percentages || {});
    setTypeDialogOpen(true);
  };

  const handleUpdateTypeProducts = async (type, productIds) => {
    try {
      await updateActivityType(type.id, { product_ids: productIds });
      toast({ title: 'Prodotti associati aggiornati' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    }
  };

  const toggleProductSelection = (productId) => {
    setNewTypeProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleDeleteType = async (type) => {
    if (type.is_system) {
      toast({ title: 'Errore', description: 'Non puoi eliminare una tipologia di sistema', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Eliminare la tipologia "${type.name}"?`)) return;
    try {
      await deleteActivityType(type.id);
      toast({ title: 'Tipologia eliminata' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile eliminare la tipologia', variant: 'destructive' });
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await updateSystemSettings({
        pm_cutoff_day: systemSettings.pm_cutoff_day,
        venditore_cutoff_day: systemSettings.venditore_cutoff_day
      });
      toast({ title: 'Impostazioni salvate' });
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile salvare le impostazioni', variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast({ title: 'Errore', description: 'Inserisci un indirizzo email', variant: 'destructive' });
      return;
    }
    setSendingTestEmail(true);
    try {
      await sendTestEmail(testEmail);
      toast({ title: 'Email inviata', description: `Email di test inviata a ${testEmail}` });
      setTestEmail('');
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile inviare l\'email', variant: 'destructive' });
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Business Unit handlers
  const handleOpenBuDialog = (bu = null) => {
    if (bu) {
      setEditingBu(bu);
      setBuFormData({
        name: bu.name,
        description: bu.description || '',
      });
    } else {
      setEditingBu(null);
      setBuFormData({ name: '', description: '' });
    }
    setBuDialogOpen(true);
  };

  const handleSaveBu = async (e) => {
    e.preventDefault();
    if (!buFormData.name.trim()) {
      toast({ title: 'Errore', description: 'Il nome è obbligatorio', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingBu) {
        await updateBusinessUnit(editingBu.id, buFormData);
        toast({ title: 'Business Unit aggiornata' });
      } else {
        await createBusinessUnit(buFormData);
        toast({ title: 'Business Unit creata' });
      }
      setBuDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBu = async (bu) => {
    if (!window.confirm(`Eliminare la Business Unit "${bu.name}"?`)) return;
    try {
      await deleteBusinessUnit(bu.id);
      toast({ title: 'Business Unit eliminata' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  // Product handlers
  const handleOpenProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductFormData({
        name: product.name,
        business_unit_id: product.business_unit_id,
        has_expiration: product.has_expiration,
        description: product.description || '',
      });
    } else {
      setEditingProduct(null);
      setProductFormData({
        name: '',
        business_unit_id: businessUnits.length > 0 ? businessUnits[0].id : '',
        has_expiration: true,
        description: '',
      });
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productFormData.name.trim()) {
      toast({ title: 'Errore', description: 'Il nome è obbligatorio', variant: 'destructive' });
      return;
    }
    if (!productFormData.business_unit_id) {
      toast({ title: 'Errore', description: 'Seleziona una Business Unit', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, productFormData);
        toast({ title: 'Prodotto aggiornato' });
      } else {
        await createProduct(productFormData);
        toast({ title: 'Prodotto creato' });
      }
      setProductDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Eliminare il prodotto "${product.name}"?`)) return;
    try {
      await deleteProduct(product.id);
      toast({ title: 'Prodotto eliminato' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  // ── Compensation Types handlers ──
  const handleOpenCompTypeDialog = (ct = null) => {
    if (ct) {
      setEditingCompType(ct);
      setCompTypeForm({ name: ct.name, bucket: ct.bucket });
    } else {
      setEditingCompType(null);
      setCompTypeForm({ name: '', bucket: 'fisso' });
    }
    setCompTypeDialogOpen(true);
  };

  const handleSaveCompType = async (e) => {
    e.preventDefault();
    if (!compTypeForm.name.trim()) return;
    setSaving(true);
    try {
      if (editingCompType) {
        await updateCompensationType(editingCompType.id, compTypeForm);
        toast({ title: 'Tipologia compenso aggiornata' });
      } else {
        await createCompensationType(compTypeForm);
        toast({ title: 'Tipologia compenso creata' });
      }
      setCompTypeDialogOpen(false);
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompType = async (ct) => {
    if (!window.confirm(`Eliminare la tipologia "${ct.name}"?`)) return;
    try {
      await deleteCompensationType(ct.id);
      toast({ title: 'Tipologia disattivata' });
      loadData();
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  // ── Product Compensation Config handlers ──
  const handleOpenProductCompDialog = async (product) => {
    setSelectedProductForComp(product);
    setEditingComp(null);
    setCompForm({ project_function: '', compensation_type_id: compensationTypes[0]?.id || '', amount: '', recurrence_type: 'una_tantum', recurrence_months: '', auto_freeze: true });
    try {
      const res = await getProductCompensations(product.id);
      setProductComps(res.data);
    } catch { setProductComps([]); }
    setProductCompDialogOpen(true);
  };

  const handleSaveProductComp = async (e) => {
    e.preventDefault();
    if (!compForm.amount || !compForm.compensation_type_id) return;
    setSaving(true);
    try {
      const payload = { ...compForm, amount: parseFloat(compForm.amount), product_id: selectedProductForComp.id, recurrence_months: compForm.recurrence_type === 'mensile_limitato' ? parseInt(compForm.recurrence_months) || 1 : null };
      if (editingComp) {
        await updateProductCompensation(selectedProductForComp.id, editingComp.id, payload);
        toast({ title: 'Compenso aggiornato' });
      } else {
        await createProductCompensation(selectedProductForComp.id, payload);
        toast({ title: 'Compenso aggiunto' });
      }
      const res = await getProductCompensations(selectedProductForComp.id);
      setProductComps(res.data);
      setEditingComp(null);
      setCompForm({ project_function: '', compensation_type_id: compensationTypes[0]?.id || '', amount: '', recurrence_type: 'una_tantum', recurrence_months: '', auto_freeze: true });
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Operazione fallita', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditProductComp = (comp) => {
    setEditingComp(comp);
    setCompForm({ project_function: comp.project_function || comp.role || '', compensation_type_id: comp.compensation_type_id, amount: String(comp.amount), recurrence_type: comp.recurrence_type, recurrence_months: comp.recurrence_months ? String(comp.recurrence_months) : '', auto_freeze: comp.auto_freeze });
  };

  const handleDeleteProductComp = async (comp) => {
    if (!window.confirm('Eliminare questo compenso?')) return;
    try {
      await deleteProductCompensation(selectedProductForComp.id, comp.id);
      toast({ title: 'Compenso eliminato' });
      const res = await getProductCompensations(selectedProductForComp.id);
      setProductComps(res.data);
    } catch (err) {
      toast({ title: 'Errore', description: err.response?.data?.detail || 'Impossibile eliminare', variant: 'destructive' });
    }
  };

  const getBuName = (buId) => {
    const bu = businessUnits.find(b => b.id === buId);
    return bu ? bu.name : 'N/A';
  };

  const getFieldTypeInfo = (type) => fieldTypes.find((f) => f.value === type) || { label: type, icon: '?' };
  const getEntityTypeInfo = (type) => entityTypes.find((e) => e.value === type) || { label: type, color: 'bg-gray-100 text-gray-700' };

  // Group fields by entity type
  const fieldsByEntity = {
    project: customFields.filter(f => f.entity_type === 'project' || !f.entity_type),
    contract: customFields.filter(f => f.entity_type === 'contract'),
    client: customFields.filter(f => f.entity_type === 'client'),
    success_fee: customFields.filter(f => f.entity_type === 'success_fee'),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
        <p className="text-gray-500">Configura il sistema</p>
      </div>

      <Tabs defaultValue="custom-fields">
        <TabsList>
          <TabsTrigger value="custom-fields" className="flex items-center gap-2">
            <Database className="w-4 h-4" />
            Campi Personalizzati
          </TabsTrigger>
          <TabsTrigger value="activity-types" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            Tipologie Attività
          </TabsTrigger>
          <TabsTrigger value="business-units" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Business Unit
          </TabsTrigger>
          <TabsTrigger value="products" className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            Prodotti
          </TabsTrigger>
          <TabsTrigger value="compensation-types" className="flex items-center gap-2">
            <Coins className="w-4 h-4" />
            Tipologie Compenso
          </TabsTrigger>
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Generale
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Notifiche
          </TabsTrigger>
          <TabsTrigger value="storage" className="flex items-center gap-2" data-testid="storage-tab-trigger">
            <HardDrive className="w-4 h-4" />
            Storage
          </TabsTrigger>
        </TabsList>

        {/* CUSTOM FIELDS TAB */}
        <TabsContent value="custom-fields">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Campi Personalizzati</CardTitle>
                  <CardDescription>Definisci campi aggiuntivi per Progetti, Contratti, Clienti e Success Fee & Vendite</CardDescription>
                </div>
                <Button onClick={handleOpenFieldDialog} data-testid="add-field-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Campo
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {customFields.length === 0 ? (
                <div className="text-center py-12">
                  <Tag className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="mt-4 text-gray-500">Nessun campo personalizzato definito</p>
                  <p className="text-sm text-gray-400">I campi personalizzati permettono di aggiungere informazioni extra</p>
                </div>
              ) : (
                <>
                  {/* Fields grouped by entity type */}
                  {entityTypes.map(entityType => {
                    const fields = fieldsByEntity[entityType.value];
                    if (fields.length === 0) return null;
                    
                    const EntityIcon = entityType.icon;
                    return (
                      <div key={entityType.value} className="space-y-3">
                        <div className="flex items-center gap-2">
                          <EntityIcon className="w-5 h-5 text-gray-500" />
                          <h3 className="font-medium">{entityType.label}</h3>
                          <Badge variant="secondary">{fields.length}</Badge>
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Nome</TableHead>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Opzioni</TableHead>
                              <TableHead>Obbligatorio</TableHead>
                              <TableHead className="w-12"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {fields.map((field) => {
                              const typeInfo = getFieldTypeInfo(field.field_type);
                              return (
                                <TableRow key={field.id} data-testid={`field-row-${field.id}`}>
                                  <TableCell className="font-medium">{field.name}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline">
                                      <span className="mr-1">{typeInfo.icon}</span>
                                      {typeInfo.label}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    {field.options?.length > 0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {field.options.slice(0, 3).map((opt) => (
                                          <Badge key={opt} variant="secondary" className="text-xs">
                                            {opt}
                                          </Badge>
                                        ))}
                                        {field.options.length > 3 && (
                                          <Badge variant="secondary" className="text-xs">
                                            +{field.options.length - 3}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {field.is_required ? (
                                      <Badge className="bg-red-100 text-red-700">Sì</Badge>
                                    ) : (
                                      <span className="text-gray-400">No</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleOpenFieldDialog(field)}
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDeleteField(field)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ACTIVITY TYPES TAB */}
        <TabsContent value="activity-types">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Tipologie di Attività</CardTitle>
                  <CardDescription>Gestisci le tipologie per le Vendite e i Success Fee. Ogni tipologia può essere associata a più Prodotti.</CardDescription>
                </div>
                <Button onClick={() => {
                  setEditingType(null);
                  setNewTypeName('');
                  setNewTypeProductIds([]);
                  setTypeDialogOpen(true);
                }} data-testid="add-type-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Tipologia
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {activityTypes.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="mt-4 text-gray-500">Nessuna tipologia definita</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome Tipologia</TableHead>
                      <TableHead>Prodotti Associati</TableHead>
                      <TableHead>Business Unit</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>% SF</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityTypes.map((type) => {
                      const associatedProducts = (type.product_ids || []).map(pid => products.find(p => p.id === pid)).filter(Boolean);
                      const associatedBUs = [...new Set(associatedProducts.map(p => businessUnits.find(bu => bu.id === p.business_unit_id)).filter(Boolean))];
                      return (
                        <TableRow key={type.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              {type.name}
                              {type.is_system && (
                                <Lock className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {associatedProducts.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {associatedProducts.map(product => (
                                    <Badge key={product.id} variant="secondary" className="text-xs">
                                      {product.name}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-amber-600">⚠️ Nessun prodotto configurato</p>
                              )}
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-6 text-xs"
                                onClick={() => handleEditType(type)}
                              >
                                <Pencil className="w-3 h-3 mr-1" /> Modifica
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>
                            {associatedBUs.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {associatedBUs.map(bu => (
                                  <Badge key={bu.id} variant="outline">{bu.name}</Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {type.is_system ? (
                              <Badge variant="secondary">Sistema</Badge>
                            ) : (
                              <Badge variant="outline">Personalizzata</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2 text-xs">
                              <span className="text-xs text-gray-500">
                                {(() => {
                                  const ff = type.function_fee_percentages || {};
                                  const entries = Object.entries(ff).filter(([,v]) => v > 0);
                                  if (entries.length === 0) {
                                    // Legacy fallback display
                                    const pm = type.pm_fee_percentage || 0;
                                    const op = type.operativo_fee_percentage || 0;
                                    if (pm === 0 && op === 0) return '0%';
                                    return `PM: ${pm}% Op: ${op}%`;
                                  }
                                  return entries.map(([k, v]) => {
                                    const fn = projectFunctions.find(f => f.value === k);
                                    return `${fn?.label || k}: ${v}%`;
                                  }).join(', ');
                                })()}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditType(type)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {!type.is_system && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteType(type)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              <div className="mt-4 p-4 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-700">
                  <strong>Importante:</strong> Ogni tipologia deve essere associata a un Prodotto per poter registrare vendite e Success Fee. 
                  Il sistema userà questa associazione per calcolare i ricavi e i costi per prodotto.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BUSINESS UNITS TAB */}
        <TabsContent value="business-units">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Business Unit</CardTitle>
                  <CardDescription>Gestisci le unità di business per raggruppare i prodotti</CardDescription>
                </div>
                <Button onClick={() => handleOpenBuDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Business Unit
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {businessUnits.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="mt-4 text-gray-500">Nessuna Business Unit definita</p>
                  <p className="text-sm text-gray-400">Le Business Unit raggruppano i prodotti per area di business</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Descrizione</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {businessUnits.map((bu) => (
                      <TableRow key={bu.id}>
                        <TableCell className="font-medium">{bu.name}</TableCell>
                        <TableCell className="text-gray-500">{bu.description || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={bu.is_active ? 'default' : 'secondary'}>
                            {bu.is_active ? 'Attiva' : 'Inattiva'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenBuDialog(bu)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteBu(bu)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRODUCTS TAB */}
        <TabsContent value="products">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Prodotti</CardTitle>
                  <CardDescription>Catalogo prodotti da associare ai contratti</CardDescription>
                </div>
                <Button onClick={() => handleOpenProductDialog()} disabled={businessUnits.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Prodotto
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {businessUnits.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="mt-4 text-gray-500">Crea prima una Business Unit</p>
                  <p className="text-sm text-gray-400">I prodotti devono essere associati a una Business Unit</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="mt-4 text-gray-500">Nessun prodotto definito</p>
                  <p className="text-sm text-gray-400">I prodotti vengono associati ai contratti con le loro date di validità</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Business Unit</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.description && (
                              <p className="text-sm text-gray-500">{product.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{getBuName(product.business_unit_id)}</Badge>
                        </TableCell>
                        <TableCell>
                          {product.has_expiration ? (
                            <Badge variant="default" className="bg-orange-100 text-orange-700">
                              <Calendar className="w-3 h-3 mr-1" />
                              Con scadenza
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              Senza scadenza
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={product.is_active ? 'default' : 'secondary'}>
                            {product.is_active ? 'Attivo' : 'Inattivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenProductCompDialog(product)} title="Configura Compensi" data-testid={`product-comp-btn-${product.id}`}>
                            <Coins className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenProductDialog(product)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              <div className="mt-4 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p><strong>Nota:</strong></p>
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  <li><strong>Prodotti con scadenza:</strong> Hanno una data di inizio e fine. Non possono essere duplicati in contratti attivi sullo stesso progetto.</li>
                  <li><strong>Prodotti senza scadenza:</strong> Servizi una tantum (audit, check-up, etc.). Possono coesistere liberamente.</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPENSATION TYPES TAB */}
        <TabsContent value="compensation-types">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Tipologie Compenso</CardTitle>
                <p className="text-sm text-gray-500 mt-1">Gestisci le tipologie di compenso con bucket Fisso/Variabile</p>
              </div>
              <Button onClick={() => handleOpenCompTypeDialog()} data-testid="add-comp-type-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuova Tipologia
              </Button>
            </CardHeader>
            <CardContent>
              {compensationTypes.length === 0 ? (
                <p className="text-gray-500">Nessuna tipologia compenso definita</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Bucket</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {compensationTypes.map(ct => (
                      <TableRow key={ct.id}>
                        <TableCell className="font-medium">{ct.name}</TableCell>
                        <TableCell>
                          <Badge variant={ct.bucket === 'fisso' ? 'default' : 'secondary'} className={ct.bucket === 'fisso' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}>
                            {ct.bucket === 'fisso' ? 'Fisso' : 'Variabile'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {ct.is_system ? <Badge variant="secondary"><Lock className="w-3 h-3 mr-1" />Sistema</Badge> : <Badge variant="outline">Custom</Badge>}
                        </TableCell>
                        <TableCell className="text-right">
                          {!ct.is_system && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleOpenCompTypeDialog(ct)}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteCompType(ct)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GENERAL TAB */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle>Impostazioni Generali</CardTitle>
              <CardDescription>Configurazioni di sistema e scadenze</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Scadenze Modifica */}
              <div className="p-4 bg-orange-50 rounded-lg">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  📅 Scadenze per Modifica Vendite/Success Fee
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Dopo queste date, PM e Venditori non potranno più modificare o inserire vendite/Success Fee per il mese precedente.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="pm_cutoff">Scadenza Project Manager</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Giorno</span>
                      <Input
                        id="pm_cutoff"
                        type="number"
                        min="1"
                        max="28"
                        value={systemSettings.pm_cutoff_day}
                        onChange={(e) => setSystemSettings({ ...systemSettings, pm_cutoff_day: parseInt(e.target.value) || 6 })}
                        className="w-20"
                      />
                      <span className="text-sm text-gray-500">del mese</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      Esempio: con giorno 6, dal 7 febbraio 00:00 il PM non può più modificare gennaio
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="venditore_cutoff">Scadenza Venditore</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Giorno</span>
                      <Input
                        id="venditore_cutoff"
                        type="number"
                        min="1"
                        max="28"
                        value={systemSettings.venditore_cutoff_day}
                        onChange={(e) => setSystemSettings({ ...systemSettings, venditore_cutoff_day: parseInt(e.target.value) || 3 })}
                        className="w-20"
                      />
                      <span className="text-sm text-gray-500">del mese</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      Esempio: con giorno 3, dal 4 febbraio 00:00 il venditore non può più modificare gennaio
                    </p>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Button onClick={handleSaveSettings} disabled={savingSettings}>
                    {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Salva Scadenze
                  </Button>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  📧 Notifiche Email
                  {emailStatus.configured ? (
                    <Badge className="bg-green-100 text-green-700">Attivo</Badge>
                  ) : (
                    <Badge variant="outline">Non configurato</Badge>
                  )}
                </h4>
                
                {emailStatus.configured ? (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600">
                      <p><strong>Provider:</strong> {emailStatus.provider}</p>
                      <p><strong>Dominio:</strong> {emailStatus.domain}</p>
                      <p><strong>Mittente:</strong> {emailStatus.sender}</p>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <Label className="text-sm">Invia Email di Test</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          type="email"
                          placeholder="email@esempio.com"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          className="max-w-xs"
                        />
                        <Button onClick={handleSendTestEmail} disabled={sendingTestEmail} size="sm">
                          {sendingTestEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Invia Test
                        </Button>
                      </div>
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Con il dominio sandbox, puoi inviare solo a indirizzi verificati su Mailgun
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Per attivare le email, configura le variabili MAILGUN_API_KEY e MAILGUN_DOMAIN nel file .env
                  </p>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Credenziali Admin Default</h4>
                <p className="text-sm text-gray-500">
                  Email: admin@salesagency.com<br />
                  Password: admin123
                </p>
                <p className="text-sm text-orange-600 mt-2">
                  ⚠️ Ricorda di cambiare la password in produzione
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Configurazione Notifiche
                  </CardTitle>
                  <CardDescription className="mt-1">Imposta chi riceve le notifiche per ogni evento</CardDescription>
                </div>
                <Button onClick={async () => {
                  setNotifSaving(true);
                  try {
                    await api.put('/notification-settings', notifSettings);
                    toast({ title: 'Impostazioni salvate' });
                  } catch (e) {
                    toast({ title: 'Errore', description: 'Impossibile salvare', variant: 'destructive' });
                  }
                  setNotifSaving(false);
                }} disabled={notifSaving}>
                  {notifSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Salva Impostazioni
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {notifSettings.map((event, eIdx) => (
                <div key={event.event_type} className="border rounded-lg p-4 space-y-4">
                  <div className="font-medium text-sm flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${event.recipients?.account_manager || event.recipients?.project_manager || event.recipients?.admin || (event.recipients?.custom_emails?.length > 0) ? 'bg-green-500' : 'bg-gray-300'}`} />
                    {event.label}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                      <Checkbox checked={event.recipients?.account_manager || false}
                        onCheckedChange={v => {
                          const next = [...notifSettings];
                          next[eIdx] = { ...next[eIdx], recipients: { ...next[eIdx].recipients, account_manager: v } };
                          setNotifSettings(next);
                        }} />
                      <span className="text-sm">Account Manager</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                      <Checkbox checked={event.recipients?.project_manager || false}
                        onCheckedChange={v => {
                          const next = [...notifSettings];
                          next[eIdx] = { ...next[eIdx], recipients: { ...next[eIdx].recipients, project_manager: v } };
                          setNotifSettings(next);
                        }} />
                      <span className="text-sm">Project Manager</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                      <Checkbox checked={event.recipients?.admin || false}
                        onCheckedChange={v => {
                          const next = [...notifSettings];
                          next[eIdx] = { ...next[eIdx], recipients: { ...next[eIdx].recipients, admin: v } };
                          setNotifSettings(next);
                        }} />
                      <span className="text-sm">Tutti gli Admin</span>
                    </label>
                  </div>
                  {/* Custom emails */}
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Email personalizzate</Label>
                    <div className="flex flex-wrap gap-2">
                      {(event.recipients?.custom_emails || []).map((email, emailIdx) => (
                        <Badge key={emailIdx} variant="outline" className="gap-1 pr-1">
                          {email}
                          <button className="ml-1 text-gray-400 hover:text-red-500" onClick={() => {
                            const next = [...notifSettings];
                            const emails = [...(next[eIdx].recipients.custom_emails || [])];
                            emails.splice(emailIdx, 1);
                            next[eIdx] = { ...next[eIdx], recipients: { ...next[eIdx].recipients, custom_emails: emails } };
                            setNotifSettings(next);
                          }}>&times;</button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="email@esempio.com" className="h-8 text-xs max-w-[250px]"
                        value={eIdx === 0 ? newCustomEmail : (eIdx === 1 ? newCustomEmail : '')}
                        onChange={e => setNewCustomEmail(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && newCustomEmail.includes('@')) {
                            e.preventDefault();
                            const next = [...notifSettings];
                            const emails = [...(next[eIdx].recipients.custom_emails || []), newCustomEmail.trim()];
                            next[eIdx] = { ...next[eIdx], recipients: { ...next[eIdx].recipients, custom_emails: emails } };
                            setNotifSettings(next);
                            setNewCustomEmail('');
                          }
                        }}
                      />
                      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => {
                        if (!newCustomEmail.includes('@')) return;
                        const next = [...notifSettings];
                        const emails = [...(next[eIdx].recipients.custom_emails || []), newCustomEmail.trim()];
                        next[eIdx] = { ...next[eIdx], recipients: { ...next[eIdx].recipients, custom_emails: emails } };
                        setNotifSettings(next);
                        setNewCustomEmail('');
                      }}>Aggiungi</Button>
                    </div>
                  </div>
                </div>
              ))}
              {notifSettings.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nessuna configurazione notifiche disponibile</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STORAGE TAB */}
        <TabsContent value="storage">
          <StorageStatsPanel />
        </TabsContent>

      </Tabs>

      {/* Activity Type Dialog */}
      <Dialog open={typeDialogOpen} onOpenChange={(open) => {
        setTypeDialogOpen(open);
        if (!open) {
          setEditingType(null);
          setNewTypeName('');
          setNewTypeProductIds([]);
          setNewTypeFuncFees({});
        }
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Modifica Tipologia' : 'Nuova Tipologia Attività'}</DialogTitle>
            <DialogDescription>
              {editingType ? 'Modifica la tipologia e i prodotti associati' : 'Crea una nuova tipologia per vendite e Success Fee'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitType} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="type_name">Nome Tipologia *</Label>
              <Input
                id="type_name"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="es. Hunting, Setting, Success Fee"
                required
                data-testid="type-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Prodotti Associati *</Label>
              <p className="text-xs text-gray-500 mb-2">
                Seleziona uno o più prodotti. Se la tipologia ha più prodotti, il sistema cercherà un contratto che contenga uno di questi.
              </p>
              <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                {products.filter(p => p.is_active).map((product) => {
                  const bu = businessUnits.find(b => b.id === product.business_unit_id);
                  const isSelected = newTypeProductIds.includes(product.id);
                  return (
                    <div 
                      key={product.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${isSelected ? 'bg-blue-50 border border-blue-200' : ''}`}
                      onClick={() => toggleProductSelection(product.id)}
                    >
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => toggleProductSelection(product.id)}
                        className="rounded"
                      />
                      <span className={isSelected ? 'font-medium' : ''}>
                        {product.name} {bu ? `(${bu.name})` : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              {newTypeProductIds.length === 0 && (
                <p className="text-xs text-amber-600">⚠️ Seleziona almeno un prodotto</p>
              )}
              {newTypeProductIds.length > 0 && (
                <p className="text-xs text-gray-500">
                  {newTypeProductIds.length} prodott{newTypeProductIds.length === 1 ? 'o' : 'i'} selezionat{newTypeProductIds.length === 1 ? 'o' : 'i'}
                </p>
              )}
            </div>
            <div className="space-y-2 border-t pt-4">
              <Label className="flex items-center gap-1"><Percent className="w-3 h-3" /> Distribuzione Success Fee</Label>
              <p className="text-xs text-gray-500">Definisci la % della success fee distribuita a ogni funzione (somma max 100%)</p>
              <div className="grid gap-2">
                {projectFunctions.map(fn => (
                  <div key={fn.value} className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded">
                    <span className="text-xs font-medium text-gray-700 flex-1 min-w-0 truncate">{fn.label}</span>
                    <Input
                      type="number" min="0" max="100" step="0.1"
                      className="w-20 h-7 text-xs text-center"
                      placeholder="0"
                      value={newTypeFuncFees[fn.value] ?? ''}
                      onWheel={e => e.target.blur()}
                      onChange={e => {
                        const val = e.target.value;
                        setNewTypeFuncFees(prev => ({
                          ...prev,
                          [fn.value]: val === '' ? 0 : parseFloat(val) || 0
                        }));
                      }}
                      data-testid={`func-fee-${fn.value}`}
                    />
                    <span className="text-[10px] text-gray-400">%</span>
                  </div>
                ))}
              </div>
              {Object.values(newTypeFuncFees).reduce((s, v) => s + (v || 0), 0) > 100 && (
                <p className="text-xs text-red-600">La somma supera il 100%</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTypeDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-type-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingType ? 'Salva Modifiche' : 'Crea Tipologia'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Custom Field Dialog */}
      <Dialog open={fieldDialogOpen} onOpenChange={(open) => {
        setFieldDialogOpen(open);
        if (!open) setEditingField(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingField ? 'Modifica Campo Personalizzato' : 'Nuovo Campo Personalizzato'}</DialogTitle>
            <DialogDescription>
              {editingField 
                ? 'Modifica il nome, le opzioni o l\'obbligatorietà del campo'
                : 'Crea un nuovo campo per Progetti, Contratti, Clienti o Success Fee & Vendite'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitField} className="space-y-4">
            <div className="space-y-2">
              <Label>Applica a *</Label>
              <Select
                value={fieldFormData.entity_type}
                onValueChange={(v) => setFieldFormData({ ...fieldFormData, entity_type: v })}
                disabled={!!editingField}
              >
                <SelectTrigger data-testid="field-entity-type">
                  <SelectValue placeholder="Seleziona entità" />
                </SelectTrigger>
                <SelectContent>
                  {entityTypes.map((t) => {
                    const Icon = t.icon;
                    return (
                      <SelectItem key={t.value} value={t.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {t.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="field_name">Nome Campo *</Label>
              <Input
                id="field_name"
                value={fieldFormData.name}
                onChange={(e) => setFieldFormData({ ...fieldFormData, name: e.target.value })}
                placeholder="es. Settore, Codice Fiscale, Budget"
                required
                data-testid="field-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo Campo *</Label>
              <Select
                value={fieldFormData.field_type}
                onValueChange={(v) => setFieldFormData({ ...fieldFormData, field_type: v, options: [] })}
                disabled={!!editingField}
              >
                <SelectTrigger data-testid="field-type">
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  {fieldTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="mr-2">{t.icon}</span>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(fieldFormData.field_type === 'select' || fieldFormData.field_type === 'multiselect') && (
              <div className="space-y-2">
                <Label>Opzioni</Label>
                <div className="flex gap-2">
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Aggiungi opzione"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddOption}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {fieldFormData.options.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {fieldFormData.options.map((opt) => (
                      <Badge key={opt} variant="secondary" className="pr-1">
                        {opt}
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(opt)}
                          className="ml-1 hover:text-red-600"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="required">Campo obbligatorio</Label>
              <Switch
                id="required"
                checked={fieldFormData.required}
                onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setFieldDialogOpen(false);
                setEditingField(null);
              }}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-field-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingField ? 'Salva Modifiche' : 'Crea Campo'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Business Unit Dialog */}
      <Dialog open={buDialogOpen} onOpenChange={setBuDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBu ? 'Modifica Business Unit' : 'Nuova Business Unit'}</DialogTitle>
            <DialogDescription>
              Le Business Unit raggruppano i prodotti per area di business
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveBu} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bu_name">Nome *</Label>
              <Input
                id="bu_name"
                value={buFormData.name}
                onChange={(e) => setBuFormData({ ...buFormData, name: e.target.value })}
                placeholder="es. Consulenza, Formazione, Marketing"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bu_description">Descrizione</Label>
              <Textarea
                id="bu_description"
                value={buFormData.description}
                onChange={(e) => setBuFormData({ ...buFormData, description: e.target.value })}
                placeholder="Descrizione opzionale"
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setBuDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingBu ? 'Salva' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle>
            <DialogDescription>
              I prodotti vengono associati ai contratti con le loro date di validità
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProduct} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="product_name">Nome *</Label>
              <Input
                id="product_name"
                value={productFormData.name}
                onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                placeholder="es. Gestione Operativa, Audit, Check-up"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="product_bu">Business Unit *</Label>
              <Select
                value={productFormData.business_unit_id}
                onValueChange={(v) => setProductFormData({ ...productFormData, business_unit_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona Business Unit" />
                </SelectTrigger>
                <SelectContent>
                  {businessUnits.filter(bu => bu.is_active).map((bu) => (
                    <SelectItem key={bu.id} value={bu.id}>{bu.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="product_description">Descrizione</Label>
              <Textarea
                id="product_description"
                value={productFormData.description}
                onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                placeholder="Descrizione opzionale"
                rows={2}
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="has_expiration" className="font-medium">Ha scadenza</Label>
                <p className="text-sm text-gray-500">
                  {productFormData.has_expiration 
                    ? 'Richiede date di inizio/fine nel contratto'
                    : 'Servizio una tantum senza date'}
                </p>
              </div>
              <Switch
                id="has_expiration"
                checked={productFormData.has_expiration}
                onCheckedChange={(checked) => setProductFormData({ ...productFormData, has_expiration: checked })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingProduct ? 'Salva' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Tipologia Compenso */}
      <Dialog open={compTypeDialogOpen} onOpenChange={setCompTypeDialogOpen}>
        <DialogContent>
          <form onSubmit={handleSaveCompType}>
            <DialogHeader>
              <DialogTitle>{editingCompType ? 'Modifica Tipologia Compenso' : 'Nuova Tipologia Compenso'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="ct-name">Nome</Label>
                <Input id="ct-name" value={compTypeForm.name} onChange={(e) => setCompTypeForm(f => ({...f, name: e.target.value}))} placeholder="Es. Trasferte" data-testid="comp-type-name" />
              </div>
              <div>
                <Label>Bucket</Label>
                <Select value={compTypeForm.bucket} onValueChange={(v) => setCompTypeForm(f => ({...f, bucket: v}))}>
                  <SelectTrigger data-testid="comp-type-bucket"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fisso">Fisso</SelectItem>
                    <SelectItem value="variabile">Variabile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompTypeDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingCompType ? 'Salva' : 'Crea'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Configurazione Compensi Prodotto */}
      <Dialog open={productCompDialogOpen} onOpenChange={setProductCompDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-emerald-600" />
              Compensi - {selectedProductForComp?.name}
            </DialogTitle>
            <p className="text-sm text-gray-500">Definisci i compensi per ruolo generati automaticamente da questo prodotto</p>
          </DialogHeader>
          <div className="space-y-4">
            {/* Lista compensi esistenti */}
            {productComps.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Funzione</TableHead>
                    <TableHead>Tipologia</TableHead>
                    <TableHead>Importo</TableHead>
                    <TableHead>Ricorrenza</TableHead>
                    <TableHead>Congel.</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productComps.map(c => {
                    const ct = compensationTypes.find(t => t.id === c.compensation_type_id);
                    const funcLabel = projectFunctions.find(f => f.value === (c.project_function || c.role))?.label || c.project_function || c.role || '-';
                    return (
                      <TableRow key={c.id}>
                        <TableCell className="font-medium">{funcLabel}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{ct?.name || 'N/A'}</Badge></TableCell>
                        <TableCell className="font-mono">{c.amount.toFixed(2)} EUR</TableCell>
                        <TableCell className="text-xs">
                          {c.recurrence_type === 'una_tantum' ? 'Una tantum' : c.recurrence_type === 'mensile' ? 'Mensile' : `Mensile (${c.recurrence_months} mesi)`}
                        </TableCell>
                        <TableCell>{c.auto_freeze ? <Badge variant="secondary" className="text-xs">Si</Badge> : <span className="text-gray-400">No</span>}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleEditProductComp(c)}><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteProductComp(c)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {/* Form nuovo/modifica compenso */}
            <form onSubmit={handleSaveProductComp} className="border rounded-lg p-4 bg-gray-50 space-y-3">
              <p className="text-sm font-medium">{editingComp ? 'Modifica compenso' : 'Aggiungi compenso'}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Funzione</Label>
                  <Select value={compForm.project_function} onValueChange={(v) => setCompForm(f => ({...f, project_function: v}))}>
                    <SelectTrigger data-testid="comp-function"><SelectValue placeholder="Seleziona funzione" /></SelectTrigger>
                    <SelectContent>
                      {projectFunctions.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tipologia</Label>
                  <Select value={compForm.compensation_type_id} onValueChange={(v) => setCompForm(f => ({...f, compensation_type_id: v}))}>
                    <SelectTrigger data-testid="comp-type"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {compensationTypes.map(ct => (
                        <SelectItem key={ct.id} value={ct.id}>{ct.name} ({ct.bucket})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Importo (EUR)</Label>
                  <Input type="number" min="0" step="0.01" value={compForm.amount} onChange={(e) => setCompForm(f => ({...f, amount: e.target.value}))} placeholder="200" data-testid="comp-amount" />
                </div>
                <div>
                  <Label className="text-xs">Ricorrenza</Label>
                  <Select value={compForm.recurrence_type} onValueChange={(v) => setCompForm(f => ({...f, recurrence_type: v}))}>
                    <SelectTrigger data-testid="comp-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="una_tantum">Una tantum</SelectItem>
                      <SelectItem value="mensile">Mensile</SelectItem>
                      <SelectItem value="mensile_limitato">Mensile limitato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {compForm.recurrence_type === 'mensile_limitato' && (
                  <div>
                    <Label className="text-xs">N. Mesi</Label>
                    <Input type="number" min="1" value={compForm.recurrence_months} onChange={(e) => setCompForm(f => ({...f, recurrence_months: e.target.value}))} placeholder="2" data-testid="comp-months" />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="auto-freeze" checked={compForm.auto_freeze} onChange={(e) => setCompForm(f => ({...f, auto_freeze: e.target.checked}))} className="rounded" />
                <Label htmlFor="auto-freeze" className="text-xs">Congelamento automatico (sospende compensi se progetto congelato/terminato)</Label>
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingComp ? 'Aggiorna' : 'Aggiungi'}</Button>
                {editingComp && <Button type="button" variant="outline" size="sm" onClick={() => { setEditingComp(null); setCompForm({ project_function: '', compensation_type_id: compensationTypes[0]?.id || '', amount: '', recurrence_type: 'una_tantum', recurrence_months: '', auto_freeze: true }); }}>Annulla</Button>}
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
